﻿import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Loader2, X } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Trans } from "react-i18next";
import { z } from "zod";
import {
  SUPPORTED_LOCALES,
  type Locale,
  type ShopHeroStyle,
  type ShopStorefrontType,
} from "@ticketnjoy/types";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { ImageUploader } from "@/components/image-uploader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

// Shop preview için host kısmı: shop.ticketnjoy.com / shop-test.ticketnjoy.com / localhost:3002
// Build-time Vite tarafından bundle'a inline edilir.
const SHOP_HOST = (import.meta.env.VITE_SHOP_BASE_URL ?? "https://shop.ticketnjoy.com").replace(
  /^https?:\/\//,
  "",
);

/**
 * Create ve edit için ortak form şeması.
 * - Slug create'te opsiyonel (auto-generate), edit'te form göstermez.
 * - HeroStyle edit-only — create'te backend default "Profile" uygular, organizer
 *   sonra Tema Ayarları kartından değiştirir.
 * - LogoUrl/CoverImageUrl sadece edit'te.
 */
const baseSchema = {
  name: z.string().min(2, "validation.nameMin").max(200),
  description: z.string().max(1000).optional().or(z.literal("")),
  defaultLocale: z.enum(SUPPORTED_LOCALES),
  contactEmail: z.string().email("validation.emailInvalid").optional().or(z.literal("")),
  contactPhone: z
    .string()
    .regex(/^\+[1-9]\d{7,14}$/, "validation.phoneE164")
    .optional()
    .or(z.literal("")),
  storefrontType: z.enum(["Link", "Embed", "Site"]),
};

const createSchema = z.object({
  ...baseSchema,
  slug: z
    .string()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "validation.slugPattern")
    .min(3)
    .max(50)
    .optional()
    .or(z.literal("")),
});

// host-only validation: scheme, port, path, query yok. Host alfanumerik + tire,
// nokta ile ayrılmış parçalardan oluşmalı. IP adresleri MVP scope dışı.
const HOST_ONLY_REGEX = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)(?:\.(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?))+$/;

const editSchema = z.object({
  ...baseSchema,
  logoUrl: z.string().url("validation.urlInvalid").optional().or(z.literal("")),
  coverImageUrl: z.string().url("validation.urlInvalid").optional().or(z.literal("")),
  heroStyle: z.enum(["Profile", "Showcase"]),
  allowedEmbedDomains: z
    .array(z.string().regex(HOST_ONLY_REGEX, "validation.hostInvalid"))
    .max(50, "validation.domainsMax"),
});

export type CreateShopFormValues = z.infer<typeof createSchema>;
export type EditShopFormValues = z.infer<typeof editSchema>;

type ShopFormProps =
  | {
      mode: "create";
      defaultValues?: Partial<CreateShopFormValues>;
      onSubmit: (values: CreateShopFormValues) => Promise<void> | void;
      isSubmitting?: boolean;
      submitLabel?: string;
    }
  | {
      mode: "edit";
      /** Edit modda zorunlu — logo/cover upload ownership check'i için. */
      shopId: string;
      defaultValues?: Partial<EditShopFormValues>;
      onSubmit: (values: EditShopFormValues) => Promise<void> | void;
      isSubmitting?: boolean;
      submitLabel?: string;
    };

export function ShopForm(props: ShopFormProps) {
  if (props.mode === "create") {
    return <CreateFormImpl {...props} />;
  }
  return <EditFormImpl {...props} />;
}

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

type CreateFormProps = Extract<ShopFormProps, { mode: "create" }>;

function CreateFormImpl({ defaultValues, onSubmit, isSubmitting, submitLabel }: CreateFormProps) {
  const t = useT("shops.form");
  const form = useForm<CreateShopFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      name: "",
      description: "",
      slug: "",
      defaultLocale: "tr",
      contactEmail: "",
      contactPhone: "",
      storefrontType: "Link",
      ...defaultValues,
    },
  });

  const storefrontType = form.watch("storefrontType");
  const showDescription = storefrontType === "Site";

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        <Section
          title={t("storefrontTypeLabel")}
          description={t("storefrontTypeHint")}
        >
          <FormField
            control={form.control}
            name="storefrontType"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="sr-only">{t("storefrontTypeLabel")}</FormLabel>
                <FormControl>
                  <StorefrontTypeRadio
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Section>

        <Section title={t("basicSection")}>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("nameLabel")} *</FormLabel>
                <FormControl>
                  <Input placeholder={t("namePlaceholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="slug"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("slugLabel")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("slugHint")} dir="ltr" {...field} />
                </FormControl>
                <FormDescription dir="ltr" className="text-start">
                  {SHOP_HOST}/<span className="font-mono">{field.value || "ankara-sahne"}</span>
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Açıklama Site dışı tiplerde public render'a yansımıyor → onboarding
              sürtünmesini düşürmek için sadece Site tipinde göster. DB'ye yazılmaz
              (formdan boş geçer) ve organizer sonradan Site'a geçerse Tema Ayarları
              kartından doldurabilir. */}
          {showDescription ? (
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("descriptionLabel")}</FormLabel>
                  <FormControl>
                    <Textarea placeholder={t("descriptionPlaceholder")} rows={3} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}

          <FormField
            control={form.control}
            name="defaultLocale"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("defaultLocaleLabel")} *</FormLabel>
                <FormControl>
                  <LocaleSelect value={field.value} onChange={field.onChange} />
                </FormControl>
                <FormDescription>{t("defaultLocaleHint")}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </Section>

        <Section title={t("contactSection")}>
          <ContactFields form={form} />
        </Section>

        <SubmitRow isSubmitting={isSubmitting} label={submitLabel ?? t("submitCreate")} />
      </form>
    </Form>
  );
}

// ---------------------------------------------------------------------------
// Edit
// ---------------------------------------------------------------------------

type EditFormProps = Extract<ShopFormProps, { mode: "edit" }>;

function EditFormImpl({ shopId, defaultValues, onSubmit, isSubmitting, submitLabel }: EditFormProps) {
  const t = useT("shops.form");
  const form = useForm<EditShopFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      name: "",
      description: "",
      defaultLocale: "tr",
      logoUrl: "",
      coverImageUrl: "",
      contactEmail: "",
      contactPhone: "",
      heroStyle: "Profile",
      storefrontType: "Link",
      allowedEmbedDomains: [],
      ...defaultValues,
    },
  });

  const storefrontType = form.watch("storefrontType");
  const isSite = storefrontType === "Site";
  const initialType = defaultValues?.storefrontType ?? "Site";
  const typeChanged = storefrontType !== initialType;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        <Section
          title={t("storefrontTypeLabel")}
          description={t("storefrontTypeHint")}
        >
          <FormField
            control={form.control}
            name="storefrontType"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="sr-only">{t("storefrontTypeLabel")}</FormLabel>
                <FormControl>
                  <StorefrontTypeRadio
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {typeChanged ? (
            <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/50 dark:text-amber-200">
              {t("storefrontTypeBranchedNote")}
            </p>
          ) : null}
        </Section>

        <Section title={t("basicSection")}>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("nameLabel")} *</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="defaultLocale"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("defaultLocaleLabel")} *</FormLabel>
                <FormControl>
                  <LocaleSelect value={field.value} onChange={field.onChange} />
                </FormControl>
                <FormDescription>{t("defaultLocaleHint")}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </Section>

        {/* Görsel ve Tema bölümleri sadece Site tipinde public render'a yansır.
            Link/Embed'de gizleniyor — değerler form state'inde korunuyor (fieldlar
            unmount olsa bile defaultValues geçerli kalır), kullanıcı Site'a geri
            dönerse aynen geri gelir. */}
        {isSite ? (
        <Section title={t("visualSection")}>
          {/* Logo kompakt (64×64 avatar) + kapak geniş; mobil stack, sm+ yanyana. */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <FormField
              control={form.control}
              name="logoUrl"
              render={({ field }) => (
                <FormItem className="sm:shrink-0">
                  <FormLabel>{t("logoUrlLabel")}</FormLabel>
                  <FormControl>
                    <ImageUploader
                      kind="ShopLogo"
                      shopId={shopId}
                      aspect="square"
                      actionsPosition="beside"
                      value={typeof field.value === "string" ? field.value : ""}
                      onChange={(url) => field.onChange(url ?? "")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="coverImageUrl"
              render={({ field }) => (
                <FormItem className="sm:flex-1">
                  <FormLabel>{t("coverImageUrlLabel")}</FormLabel>
                  <FormControl>
                    <ImageUploader
                      kind="ShopCover"
                      shopId={shopId}
                      aspect="cover"
                      value={typeof field.value === "string" ? field.value : ""}
                      onChange={(url) => field.onChange(url ?? "")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("descriptionLabel")}</FormLabel>
                <FormControl>
                  <Textarea rows={3} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Section>
        ) : null}

        <Section title={t("contactSection")}>
          <ContactFields form={form} />
        </Section>

        {isSite ? (
          <Section
            title={t("themeSection")}
            description={t("themeDescription")}
          >
            <FormField
              control={form.control}
              name="heroStyle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">{t("heroStyleLabel")}</FormLabel>
                  <FormControl>
                    <HeroStyleRadio value={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Section>
        ) : null}

        {/* Embed güvenliği — event-level embed widget shop tipinden bağımsız çalıştığı
            için bu bölümü her tip mağazada gösteriyoruz. Embed iframe'in
            `tn_return` URL'inde sadece bu listedeki host'lara redirect yapılır. */}
        <Section
          title={t("embedSecurity")}
          description={t("embedSecurityDescription")}
        >
          <FormField
            control={form.control}
            name="allowedEmbedDomains"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("allowedDomains")}</FormLabel>
                <FormControl>
                  <DomainChipsInput
                    value={field.value ?? []}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormDescription>
                  <Trans
                    i18nKey="shops.form.allowedDomainsHint"
                    components={{ code: <code className="font-mono" /> }}
                  />
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </Section>

        <SubmitRow isSubmitting={isSubmitting} label={submitLabel ?? t("submitUpdate")} />
      </form>
    </Form>
  );
}

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4 rounded-lg border p-4">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold">{title}</h3>
        {description ? (
          <p className="text-xs text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function ContactFields({ form }: { form: any }) {
  const t = useT("shops.form");
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <FormField
        control={form.control}
        name="contactPhone"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("contactPhoneLabel")}</FormLabel>
            <FormControl>
              <Input placeholder="+905551234567" dir="ltr" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="contactEmail"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("contactEmailLabel")}</FormLabel>
            <FormControl>
              <Input type="email" placeholder={t("contactEmailPlaceholder")} dir="ltr" {...field} />
            </FormControl>
            <FormDescription>{t("contactEmailHint")}</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
/* eslint-enable @typescript-eslint/no-explicit-any */

function SubmitRow({ isSubmitting, label }: { isSubmitting?: boolean; label: string }) {
  const tCommon = useT("common");
  return (
    <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
      {isSubmitting && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
      {isSubmitting ? tCommon("saving") : label}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Hero style radio cards
// ---------------------------------------------------------------------------

const HERO_STYLE_OPTIONS: { value: ShopHeroStyle; preview: React.ReactNode }[] = [
  { value: "Profile", preview: <ProfilePreview /> },
  { value: "Showcase", preview: <ShowcasePreview /> },
];

function HeroStyleRadio({
  value,
  onChange,
}: {
  value: ShopHeroStyle;
  onChange: (v: ShopHeroStyle) => void;
}) {
  const t = useT("shops.form");
  return (
    <div
      role="radiogroup"
      aria-label={t("heroStyleLabel")}
      className="grid gap-3 sm:grid-cols-2"
    >
      {HERO_STYLE_OPTIONS.map((option) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              "group relative flex flex-col gap-3 rounded-lg border p-3 text-start transition-colors",
              "hover:border-foreground/30",
              selected
                ? "border-primary ring-2 ring-primary/20"
                : "border-input",
            )}
          >
            {selected ? (
              <span className="absolute end-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Check className="h-3 w-3" />
              </span>
            ) : null}
            {option.preview}
            <div>
              <div className="text-sm font-medium">{t(`heroStyles.${option.value}.title`)}</div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {t(`heroStyles.${option.value}.description`)}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/** Profil varyantı için mini wireframe: kapak + sol altta taşan avatar + isim satırı. */
function ProfilePreview() {
  return (
    <div className="relative overflow-hidden rounded-md border bg-muted">
      <div className="h-14 w-full bg-gradient-to-r from-slate-300 to-slate-400" />
      <div className="flex items-end gap-2 px-3 pb-2">
        <div className="-mt-3 h-8 w-8 shrink-0 rounded-full border-2 border-background bg-slate-500" />
        <div className="flex-1 py-1">
          <div className="h-1.5 w-20 rounded bg-foreground/70" />
          <div className="mt-1 h-1 w-28 rounded bg-foreground/30" />
        </div>
      </div>
    </div>
  );
}

/** Vitrin varyantı için mini wireframe: yüksek kapak + dip gradient + beyaz isim satırı. */
function ShowcasePreview() {
  return (
    <div className="relative overflow-hidden rounded-md border bg-muted">
      <div className="h-20 w-full bg-gradient-to-br from-slate-600 to-slate-800" />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/70 to-transparent"
      />
      <div className="absolute inset-x-3 bottom-2">
        <div className="h-1.5 w-24 rounded bg-white/95" />
        <div className="mt-1 h-1 w-32 rounded bg-white/60" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Storefront type radio cards
// ---------------------------------------------------------------------------

function StorefrontTypeRadio({
  value,
  onChange,
}: {
  value: ShopStorefrontType;
  onChange: (v: ShopStorefrontType) => void;
}) {
  const t = useT("shops.form.storefrontType");
  const tForm = useT("shops.form");
  const options: {
    value: ShopStorefrontType;
    title: string;
    description: string;
    preview: React.ReactNode;
  }[] = [
    {
      value: "Link",
      title: t("linkTitle"),
      description: t("linkDescription"),
      preview: <LinkPreview />,
    },
    {
      value: "Embed",
      title: t("embedTitle"),
      description: t("embedDescription"),
      preview: <EmbedPreview />,
    },
    {
      value: "Site",
      title: t("siteTitle"),
      description: t("siteDescription"),
      preview: <SitePreview />,
    },
  ];

  return (
    <div
      role="radiogroup"
      aria-label={tForm("storefrontTypeLabel")}
      className="grid gap-3 sm:grid-cols-3"
    >
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              "group relative flex flex-col gap-3 rounded-lg border p-3 text-start transition-colors",
              "hover:border-foreground/30",
              selected
                ? "border-primary ring-2 ring-primary/20"
                : "border-input",
            )}
          >
            {selected ? (
              <span className="absolute end-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Check className="h-3 w-3" />
              </span>
            ) : null}
            {option.preview}
            <div>
              <div className="text-sm font-medium">{option.title}</div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {option.description}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/** Link preview — TN markalı sade şablon: header + tek event kart. */
function LinkPreview() {
  return (
    <div className="relative overflow-hidden rounded-md border bg-background">
      <div className="flex h-5 items-center gap-1.5 border-b bg-card px-2">
        <div className="h-1 w-6 rounded bg-foreground/70" />
        <div className="h-1 w-1 rounded-full bg-muted-foreground/50" />
        <div className="h-1 w-10 rounded bg-foreground/40" />
      </div>
      <div className="space-y-1.5 p-2">
        <div className="rounded border p-1.5">
          <div className="h-1 w-16 rounded bg-foreground/70" />
          <div className="mt-1 h-1 w-12 rounded bg-foreground/30" />
        </div>
      </div>
    </div>
  );
}

/** Embed preview — organizer site içinde gömülü iframe (kesik kenar). */
function EmbedPreview() {
  return (
    <div className="relative overflow-hidden rounded-md border bg-muted/30 p-1.5">
      <div className="mb-1 flex gap-1">
        <div className="h-1 w-1 rounded-full bg-muted-foreground/50" />
        <div className="h-1 w-1 rounded-full bg-muted-foreground/50" />
        <div className="h-1 w-1 rounded-full bg-muted-foreground/50" />
      </div>
      <div className="rounded border-2 border-dashed border-primary/40 bg-background p-1.5">
        <div className="h-1 w-12 rounded bg-foreground/60" />
        <div className="mt-1 h-1 w-8 rounded bg-foreground/30" />
        <div className="mt-1.5 h-2 w-full rounded bg-primary/70" />
      </div>
    </div>
  );
}

/** Site preview — tam markalı zengin layout (kapak + grid). */
function SitePreview() {
  return (
    <div className="relative overflow-hidden rounded-md border bg-muted">
      <div className="h-10 w-full bg-gradient-to-br from-slate-500 to-slate-700" />
      <div className="grid gap-1 p-1.5">
        <div className="h-1 w-12 rounded bg-foreground/70" />
        <div className="grid grid-cols-2 gap-1">
          <div className="h-3 rounded bg-foreground/15" />
          <div className="h-3 rounded bg-foreground/15" />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Selects
// ---------------------------------------------------------------------------
// Native <select> geçici — shadcn Select eklenince migrate edilir.

const LOCALE_LABELS: Record<Locale, string> = {
  tr: "🇹🇷 Türkçe",
  en: "🇬🇧 English",
  ar: "🇸🇦 العربية",
};

function LocaleSelect({
  value,
  onChange,
}: {
  value: Locale;
  onChange: (v: Locale) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as Locale)}
      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm"
    >
      {SUPPORTED_LOCALES.map((l) => (
        <option key={l} value={l}>
          {LOCALE_LABELS[l]}
        </option>
      ))}
    </select>
  );
}

// ---------------------------------------------------------------------------
// Domain chips input
// ---------------------------------------------------------------------------
// Enter / virgül / boşluk → chip ekle. Backspace boşken → son chip'i sil.
// Yapıştırılan metin satır/virgül ile bölünür. Geçersiz host'lar local error
// göstererek girişi reddeder. Backend ayrıca normalize ediyor (lowercased,
// scheme/port stripping); buradaki kontrol sadece UX hızlandırıcı.

function DomainChipsInput({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const t = useT("shops.form");
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  function tryAdd(raw: string) {
    const cleaned = raw.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    if (!cleaned) return;
    if (!HOST_ONLY_REGEX.test(cleaned)) {
      setError(t("domainInvalid", { host: cleaned }));
      return;
    }
    if (value.includes(cleaned)) {
      setError(t("domainDuplicate"));
      return;
    }
    if (value.length >= 50) {
      setError(t("domainLimit"));
      return;
    }
    setError(null);
    onChange([...value, cleaned]);
    setDraft("");
  }

  function flushDraft() {
    if (draft.trim()) tryAdd(draft);
  }

  function remove(idx: number) {
    setError(null);
    onChange(value.filter((_, i) => i !== idx));
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      flushDraft();
      return;
    }
    if (e.key === "Backspace" && !draft && value.length > 0) {
      remove(value.length - 1);
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData("text");
    if (!/[\s,;]/.test(text)) return;
    e.preventDefault();
    text
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach(tryAdd);
  }

  return (
    <div className="space-y-2">
      <div
        className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-2 py-1.5 text-sm shadow-xs transition-colors focus-within:ring-1 focus-within:ring-ring"
        onClick={(e) => {
          // Container'a tıklayınca odak input'a gitsin (chips dışı boş alan tıklamada).
          const input = (e.currentTarget as HTMLDivElement).querySelector("input");
          input?.focus();
        }}
      >
        {value.map((domain, idx) => (
          <span
            key={domain}
            className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 font-mono text-xs"
          >
            {domain}
            <button
              type="button"
              onClick={() => remove(idx)}
              className="rounded-sm text-muted-foreground transition hover:text-destructive"
              aria-label={t("removeDomain", { domain })}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={handleKey}
          onPaste={handlePaste}
          onBlur={flushDraft}
          placeholder={value.length === 0 ? "konser-sitem.com" : ""}
          dir="ltr"
          className="flex-1 bg-transparent px-1 py-0.5 text-sm outline-none placeholder:text-muted-foreground"
        />
      </div>
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : null}
    </div>
  );
}
